import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSun, FiCloud, FiCloudRain, FiCloudSnow, FiWind, FiDroplet, FiEye, FiThermometer } = FiIcons;

const WeatherCard = ({ weatherData, loading, error, city }) => {
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white/20 backdrop-blur-lg rounded-2xl p-8 text-center"
      >
        <div className="animate-spin w-12 h-12 border-4 border-white/30 border-t-white rounded-full mx-auto mb-4"></div>
        <p className="text-white text-lg">Loading weather data...</p>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-red-500/20 backdrop-blur-lg rounded-2xl p-8 text-center border border-red-300/30"
      >
        <SafeIcon icon={FiCloud} className="w-12 h-12 text-red-300 mx-auto mb-4" />
        <p className="text-red-200 text-lg">{error}</p>
      </motion.div>
    );
  }

  if (!weatherData) return null;

  const getWeatherIcon = (condition) => {
    const lowerCondition = condition.toLowerCase();
    if (lowerCondition.includes('rain')) return FiCloudRain;
    if (lowerCondition.includes('cloud')) return FiCloud;
    if (lowerCondition.includes('snow')) return FiCloudSnow;
    return FiSun;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white/20 backdrop-blur-lg rounded-2xl p-8 border border-white/30 shadow-2xl"
    >
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">{city}</h2>
        <p className="text-white/80 text-lg capitalize">{weatherData.weather[0].description}</p>
      </div>

      <div className="flex items-center justify-center mb-8">
        <SafeIcon
          icon={getWeatherIcon(weatherData.weather[0].main)}
          className="w-24 h-24 text-white mr-6"
        />
        <div className="text-center">
          <div className="text-6xl font-bold text-white mb-2">
            {Math.round(weatherData.main.temp)}°C
          </div>
          <div className="text-white/80">
            Feels like {Math.round(weatherData.main.feels_like)}°C
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white/10 rounded-lg p-4 text-center">
          <SafeIcon icon={FiWind} className="w-6 h-6 text-white mx-auto mb-2" />
          <p className="text-white/80 text-sm">Wind</p>
          <p className="text-white font-semibold">{weatherData.wind.speed} m/s</p>
        </div>
        
        <div className="bg-white/10 rounded-lg p-4 text-center">
          <SafeIcon icon={FiDroplet} className="w-6 h-6 text-white mx-auto mb-2" />
          <p className="text-white/80 text-sm">Humidity</p>
          <p className="text-white font-semibold">{weatherData.main.humidity}%</p>
        </div>
        
        <div className="bg-white/10 rounded-lg p-4 text-center">
          <SafeIcon icon={FiEye} className="w-6 h-6 text-white mx-auto mb-2" />
          <p className="text-white/80 text-sm">Visibility</p>
          <p className="text-white font-semibold">{(weatherData.visibility / 1000).toFixed(1)} km</p>
        </div>
        
        <div className="bg-white/10 rounded-lg p-4 text-center">
          <SafeIcon icon={FiThermometer} className="w-6 h-6 text-white mx-auto mb-2" />
          <p className="text-white/80 text-sm">Pressure</p>
          <p className="text-white font-semibold">{weatherData.main.pressure} hPa</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/10 rounded-lg p-4 text-center">
          <SafeIcon icon={FiSun} className="w-6 h-6 text-yellow-300 mx-auto mb-2" />
          <p className="text-white/80 text-sm">Sunrise</p>
          <p className="text-white font-semibold">{formatTime(weatherData.sys.sunrise)}</p>
        </div>
        
        <div className="bg-white/10 rounded-lg p-4 text-center">
          <SafeIcon icon={FiSun} className="w-6 h-6 text-orange-300 mx-auto mb-2" />
          <p className="text-white/80 text-sm">Sunset</p>
          <p className="text-white font-semibold">{formatTime(weatherData.sys.sunset)}</p>
        </div>
      </div>
    </motion.div>
  );
};

export default WeatherCard;